import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { idpApi, courseApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { EmptyState, Spinner } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  SparklesIcon,
  CheckBadgeIcon,
  XMarkIcon,
  PlusIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

const STATUS_LABELS = {
  draft:     { label: 'Draft',     color: 'bg-gray-100 text-gray-700' },
  active:    { label: 'Active',    color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
};

function TagInput({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-800 text-xs rounded">
            {t}
            <button type="button" onClick={() => onChange(value.filter(x => x !== t))} className="hover:bg-amber-100 rounded">
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder={placeholder}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" onClick={add} className="btn-secondary"><PlusIcon className="w-4 h-4" />Add</button>
      </div>
    </div>
  );
}

export default function IdpPage() {
  const { user } = useAuth();
  const qc       = useQueryClient();

  const { data: idp, isLoading } = useQuery({
    queryKey: ['my-idp'],
    queryFn:  () => idpApi.mine().then(r => r.data),
  });

  const [form, setForm] = useState({
    short_term_goal: '',
    long_term_goal:  '',
    competency_gaps: [],
  });

  useEffect(() => {
    if (idp) {
      setForm({
        short_term_goal: idp.short_term_goal || '',
        long_term_goal:  idp.long_term_goal  || '',
        competency_gaps: idp.competency_gaps || [],
      });
    }
  }, [idp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: (data) => idp ? idpApi.update(idp.id, data) : idpApi.create(data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-idp'] }); toast.success('IDP saved'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to save'),
  });

  const recommendMut = useMutation({
    mutationFn: () => idpApi.generateRecommendations(idp.id),
    onSuccess:  (r) => { qc.invalidateQueries({ queryKey: ['my-idp'] }); toast.success(`${r.data.matched ?? 0} courses matched to your gaps`); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to generate recommendations'),
  });

  const endorseMut = useMutation({
    mutationFn: () => idpApi.endorse(idp.id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-idp'] }); toast.success('IDP endorsed'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to endorse'),
  });

  const enrolMut = useMutation({
    mutationFn: (courseId) => courseApi.enroll(courseId),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-enrollments'] }); toast.success('Enrolled'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to enrol'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const canManage = !!user?.employee_id;
  const statusStyle = STATUS_LABELS[idp?.status ?? 'draft'];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="card p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Your Individual Development Plan</h2>
              <p className="text-sm text-gray-500 mt-0.5">Capture where you want to grow. Your manager endorses it, and courses auto-match your gaps.</p>
            </div>
            {idp && (
              <span className={clsx('inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold', statusStyle.color)}>
                {statusStyle.label}
              </span>
            )}
          </div>

          {!canManage ? (
            <EmptyState title="No employee profile linked" description="Your user account must be linked to an employee record to manage an IDP." />
          ) : (
            <div className="space-y-4">
              <div>
                <label className="label">Short-term goal (6 months)</label>
                <textarea
                  className="input h-20 resize-none"
                  value={form.short_term_goal}
                  onChange={e => setForm({ ...form, short_term_goal: e.target.value })}
                  placeholder="Ship the payments refactor as tech lead; run weekly design reviews."
                />
              </div>
              <div>
                <label className="label">Long-term goal (18 months)</label>
                <textarea
                  className="input h-20 resize-none"
                  value={form.long_term_goal}
                  onChange={e => setForm({ ...form, long_term_goal: e.target.value })}
                  placeholder="Promote to Staff Engineer; lead a cross-functional initiative end-to-end."
                />
              </div>
              <div>
                <label className="label">Competency gaps</label>
                <p className="text-xs text-gray-500 mb-2">Tags power auto-recommendations — use the same words as course competency tags (e.g. leadership, python, system-design).</p>
                <TagInput
                  value={form.competency_gaps}
                  onChange={(tags) => setForm({ ...form, competency_gaps: tags })}
                  placeholder="Add a gap and press Enter"
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 text-sm">
                  {idp?.manager_endorsed ? (
                    <span className="inline-flex items-center gap-1 text-green-700">
                      <CheckBadgeIcon className="w-4 h-4" />
                      Endorsed by manager {idp.manager_endorsed_at && new Date(idp.manager_endorsed_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-gray-500">Awaiting manager endorsement</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveMut.mutate(form)} disabled={saveMut.isPending} className="btn-primary">
                    {saveMut.isPending ? 'Saving…' : (idp ? 'Save changes' : 'Create IDP')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {idp && (
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-purple-500" />
                <h3 className="text-base font-semibold text-gray-900">Recommended courses</h3>
              </div>
              <button onClick={() => recommendMut.mutate()} disabled={recommendMut.isPending || form.competency_gaps.length === 0} className="btn-secondary">
                {recommendMut.isPending ? 'Matching…' : 'Get recommendations'}
              </button>
            </div>

            {(!idp.recommendations || idp.recommendations.length === 0) ? (
              <EmptyState
                title="No recommendations yet"
                description={form.competency_gaps.length === 0
                  ? 'Add competency gap tags above, then click "Get recommendations" to match courses.'
                  : 'Click "Get recommendations" to match courses to your competency gaps.'}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {idp.recommendations.map(r => (
                  <div key={r.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900">{r.course?.title}</h4>
                      {r.auto_generated && <LightBulbIcon className="w-4 h-4 text-amber-500 shrink-0" title="Auto-matched" />}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">{r.course?.description}</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(r.course?.competency_tags || []).slice(0,3).map(t => (
                        <span key={t} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded">{t}</span>
                      ))}
                    </div>
                    <button onClick={() => enrolMut.mutate(r.course_id)} disabled={enrolMut.isPending} className="btn-primary btn-sm w-full justify-center">
                      Enrol
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">How this works</h3>
          <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
            <li>Describe where you want to be in 6 and 18 months.</li>
            <li>Tag the competencies you need to build.</li>
            <li>Click <span className="font-medium">Get recommendations</span> to auto-match courses.</li>
            <li>Your manager reviews and endorses — unlocking the IDP as active.</li>
          </ol>
        </div>

        {idp && !idp.manager_endorsed && (
          <div className="card p-5 bg-amber-50 border-amber-200">
            <p className="text-sm text-amber-900 mb-3">If you're viewing this IDP as a manager of this employee, you can endorse it here.</p>
            <button onClick={() => endorseMut.mutate()} disabled={endorseMut.isPending} className="btn-primary w-full justify-center">
              {endorseMut.isPending ? 'Endorsing…' : 'Endorse as manager'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
